import React from "react";
import "./ItemsSelect.scss";
import Dialog from "../../../../../util/Dialog";

import ProposalItem from "../../../../../models/ProposalItem";
import { useProposalContext } from "../../../../../hooks/useProposalContext";
import ItemBulkDownload from "../ItemBulkDownload";
import PageContext from "../../../../../contexts/PageContext";
import ApiKeyContext from "../../../../../contexts/ApiKeyContext";
import LiveApiClient from "../../../../../api/LiveApiClient";
import { SidebarContentType, useSidebarContext } from "../../../../../hooks/useSidebarContext";
import { useDialogContext } from "../../../../../hooks/useDialogContext";
import { ArrayUtil } from "../../../../../util/Array.util";
import LoadMask from "../../../../../components/LoadMask";
import { StringUtil } from "../../../../../util/String.util";
import Proposal from "../../../../../models/Proposal";
import ProposalDetails, {
	Paths as ProposalDetailsPaths
} from "../../../../protected/pages/organizationAdmin/sections/proposals/components/ProposalDetails";
import { format } from "../../../../../util/Functions";
import Swal from "sweetalert2";
import CatalogContext from "../../../../../contexts/CatalogContext";

export default function ItemsSelect({ records, selected, setSelected, setIsSaving, enableBulkDownload, onChange }) {
	const { currentProposal, setCurrentProposal, createNewProposal } = useProposalContext();
	const SidebarContext = useSidebarContext();
	const [showOptions, setShowOptions] = React.useState(false);
	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	const { currentPage } = React.useContext(PageContext);
	const [loadingMessage, setLoadingMessage] = React.useState(undefined);
	const DialogContext = useDialogContext();
	const [catalog] = React.useContext(CatalogContext);

	const selectAll = () => {
		setSelected(records);
		onChange?.();
	};
	const deselectAll = () => {
		setSelected([]);
		onChange?.();
	}

	const selectOrCreateNewProposal = async (text = undefined, openCurrentProposal = false) => {
		const result = await Dialog.fire({
			html: text ?? "",
			icon: "warning",
			showCloseButton: true,
			showDenyButton: true,
			showCancelButton: openCurrentProposal,
			confirmButtonText: "Create New Proposal",
			denyButtonText: 'Select Existing',
			cancelButtonText: 'Open Current',
			denyButtonColor: '#09f',
		});

		if (result.isConfirmed) {
			const proposal = await createNewProposal();
			return { action: "create", proposal };
		} else if (result.isDenied) {
			return { action: "select" };
		} else if (result.isDismissed) {
			const reason = result.dismiss.toString();
			
			if (reason === 'cancel') {
				return { action: "open-current" };
			} else if (reason === 'backdrop' || reason === 'close') {
				return { action: "cancel" };
			}
		}
	};
	
	const addSelectedToProposal = async proposalOverride => {
		let proposalToUse = proposalOverride ?? currentProposal;
		
		const proposalModalTexts = {
			noProposal: "Please select or create a proposal.",
			pending: `</br></br>Proposal "${proposalToUse?.name}" is pending and can't be updated.</br></br>If you would like to change this Proposal, it must be returned to a Draft state.`,
			approved: `</br></br>Proposal "${proposalToUse?.name}" is approved and can't be updated. </br></br>Please create new Proposal or select an existing one.`
		}
		
		const getProposalModalText = () => {
			if(!proposalToUse) return proposalModalTexts.noProposal;
			if(proposalToUse?.consented) return proposalModalTexts.pending;
			if(proposalToUse?.approved) return proposalModalTexts.approved;
		}
		
		if (!proposalToUse || proposalToUse?.consented || proposalToUse?.approved) {
			const { action, proposal } = await selectOrCreateNewProposal(getProposalModalText(), proposalToUse?.consented);
			if (action === "create" && proposal) {
				setCurrentProposal(proposal);
				proposalToUse = proposal;
			}
			else if (action === "select") {
				const { instance } = await DialogContext.open({
					heading: `Select Proposal`,
					content: <ProposalSelector onSelect={p => {
						instance.close();
						addSelectedToProposal(p);
					}} />,
					withInstance: true,
				});
				
				return;
			} 
			else if (action === 'open-current'){
				SidebarContext.open(SidebarContentType.Proposal);
			}
			else {
				return;
			}
		}

		const itemLabel = StringUtil.Pluralize(selected.length, "Item");
		
		const doSave = async quantity => {
			setIsSaving(true);

			const proposalItems = selected.reduce((acc, item) => {
				const currentItem = proposalToUse.items.find(cur => cur.item.id === item.id || cur.item === item.id);

				const proposalItem = currentItem ?? new ProposalItem();
				proposalItem.item = item.id;
				proposalItem.proposal = proposalToUse?.id;
				proposalItem.quantity = (proposalItem?.quantity ?? 0) + +quantity;

				acc.push(proposalItem);
				return acc;
			}, []);
			
			let proposalItemsLoaded = 0;
			const chunks = ArrayUtil.Collate(proposalItems, 8);
			setLoadingMessage("Saving");
			for (let items of chunks) {
				await Promise.allSettled(items.map(it => it.save()));
				proposalItemsLoaded += items.length;
				const percentageDone = Math.floor(proposalItemsLoaded / proposalItems.length * 100);
				setLoadingMessage(`Saving... ${percentageDone}%`);
			}
			setLoadingMessage(null);
			
			setIsSaving(false);
			proposalToUse.addItems(proposalItems);
			setCurrentProposal(proposalToUse);
			SidebarContext.open(SidebarContentType.Proposal);
		};
		
		const result = !proposalToUse?.approved && !proposalToUse?.consented && await Dialog.fire({
			title: 'Enter a quantity',
			input: 'number',
			inputLabel: 'Quantity',
			inputValue: 1,
			inputAttributes: {
				min: 1,
			},
			showCancelButton: true,
			confirmButtonText: `Save ${itemLabel}`,
			showClass: {
				popup: "proposal-enter-quantity",
			},
			didOpen: () => {
				Swal.getInput().select();
			},
		});
		if (result.isConfirmed && +result.value !== 0) {
			await doSave(result.value);
			deselectAll();
		}
	};

	async function log_bulk_item_download() {
		const url = window.location.href;
		const url_object = new URL(url);
		const slug = url_object.pathname + url_object.search;

		//get only id's of selected items to log
		let selected_ids = [];
		selected.forEach((item) => {
			selected_ids.push(item.id);
		})
		const selected_object = { items: selected_ids }
		const payload = {
			page: currentPage.id,
			action: 'bulk-download',
			details: selected_object,
			url,
			slug,
			type: currentPage.type?.code
		};

		LiveApiClient.post('/Catalog_Activity', payload, {
			headers: {
				"api-key": API_KEY_CONTEXT.apiKey,
				account: API_KEY_CONTEXT.account
			}
		});
	}
	
	if (!Proposal.CanChange(catalog)) {
		return;
	}
	
	return (
		<>
			<LoadMask show={!!loadingMessage} message={loadingMessage} />
			
			<div className={
				"select-options"
				+ (selected.length ? ' has-selections' : '')
				+ (showOptions ? ' show-options' : '')
			}>
				<div className="select-primary-options">
					<button
						className="select-all btn btn-primary"
						aria-label="Select All"
						onClick={() => {
							selectAll();
							setShowOptions(true);
						}}
					>
						Select All
					</button>
					{selected.length !== 0 && (
						<>
							<button
								aria-label="Add Selected"
								className="add-to-proposal btn btn-primary"
								onClick={() => addSelectedToProposal()}
							>
								Add to Order{selected.length ? ` (${selected.length})` : ''}
							</button>
							
							<button
								aria-label="Show More Options"
								className="toggle-options btn"
								onClick={() => setShowOptions(!showOptions)}
							/>
						</>
					)}
				</div>
				<div className="select-additional-options">
					<button
						aria-label="Deselect All"
						className="deselect-all btn"
						onClick={() => {
							deselectAll();
							setShowOptions(false);
						}}
					>
						Deselect All
					</button>
					{enableBulkDownload && (
						<ItemBulkDownload records={selected} onDownload={() => {
							log_bulk_item_download();
							setShowOptions(false);
						}} />
					)}
				</div>
			</div>
		</>
	);
}

export const ProposalSelector = ({ onSelect }) => {
	const [proposals, setProposals] = React.useState(undefined);
	const DialogContext = useDialogContext();
	
	const init = async () => {
		const proposals = await Proposal.SearchBy({
			filter: [
				{ property: "approved", value: null },
				{ property: "consented", value: null },
			],
			order: ["created_on DESC", "name"],
			paths: ProposalDetailsPaths.Proposal,
		});
		
		setProposals(proposals);
	}
	
	// needs to be called like this cuz useEffect can only return a function
	React.useEffect(() => {
		init()
	}, []);
	
	const onPicked = async proposal => {
		const { instance, result } = await DialogContext.open({
			content: <ProposalDetails proposal={proposal} />,
			className: 'proposal-details-dialog',
			withInstance: true,
			actions: [
				{ key: "select", label: "Select", variant: "primary" },
			]
		});
		
		const action = await result;
		
		if (action === "select") {
			instance.close();
			onSelect?.(proposal);
		}
	}
	
	const makeSpacing = React.useCallback(count => {
		const nbs = [];
		for (let i = 0; i < count; i++) {
			nbs.push("..");
		}
		return nbs.join("");
	}, []);
	
	const longestTitle = proposals?.reduce((acc, it) => it.name.length > acc.length ? it.name : acc, "");
	
	const makeLabel = (proposal) => {
		const parts = [
			`${proposal.name} `,
			makeSpacing(longestTitle.length + 4 - proposal.name.length),
			proposal.created_by_profile && ` ${proposal.created_by_profile.given_name} ${proposal.created_by_profile.family_name} | `,
			format(proposal.created_on, { type: "date" }),
		].filter(it=>it);
		
		return parts.join("");
	}
	
	return (
		<div data-component="ProposalSelector">
			<select onChangeCapture={e => {
				const id = e.target.value;
				if (id) {
					const proposal = proposals.find(p => p.id === +id);
					onPicked(proposal);
				}
			}}>
				{!proposals?.length && (
					<option value="">Loading Proposals...</option>
				)}
				
				{!!proposals?.length && (
					<>
						<option value="">Select Proposal</option>
						{proposals.map(it => (
							<option key={it.id} value={it.id}>{makeLabel(it)}</option>
						))}
					</>
				)}
			</select>
		</div>
	);
}